import React, { Fragment, useEffect, useState, useContext } from "react";
import {
  Animate,
  AuthContext,
  Row,
  useAPI,
  Loader,
  Card,
  Grid,
  Image,
  Button,
  Message,
  Switch,
  ViewContext,
} from "components/lib";
import { useParams } from "react-router-dom";
import Axios from "axios";
import { SubNav } from "components/lib";
import jsPDF from "jspdf";
import logo from "../../assets/IdentityCheck_Logo_RGB_Purple.png";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPaste } from "@fortawesome/free-solid-svg-icons";
import { buildVerificationNavBarItems } from "./nav-bar-items";
import { canResendVerification } from "./resend-gate";
import { veriffMediaUrl } from "./media-url";
import {
  derivePdfAvailability,
  isUsableMediaResponse,
  formatSanctionsRecord,
} from "./pdf-report";
const Settings = require("settings.json");

function deriveTranche2RiskStatus(verificationInfo, riskAssessment) {
  if (riskAssessment?.status) return riskAssessment.status;
  if (!verificationInfo) return "consent_pending";

  if (verificationInfo.response_type === "tranche2_intake_v1") {
    if (["positive", "negative", "review"].includes(verificationInfo.result)) {
      return "awaiting_review";
    }
    return "awaiting_biometric";
  }

  if (verificationInfo.response_type === "tranche2_consent_v1") {
    return "awaiting_intake";
  }

  return "consent_pending";
}

export function VerificationView(props) {
  const params = useParams();
  const [imageDataFetched, setImageDataFetched] = useState(false);
  const [imagesResponse, setImgagesResponse] = useState([]);
  const [imagesUnavailable, setImagesUnavailable] = useState(false);
  const [navBarItems, setNavBarItems] = useState([]);
  const [verificationData, setVerificationData] = useState({});
  const [showPdfOptions, setShowPdfOptions] = useState(false);
  // Bumped after a resend so a corrected address is reflected without a
  // page reload -- a reload would discard the Form's success toast, which
  // fires after the modal callback.
  const [verificationReloadKey, setVerificationReloadKey] = useState(0);
  const [pdfOptions, setPdfOptions] = useState({
    images: true,
    sanctions: false,
    adverseMedia: false,
  });

  const verification = useAPI(
    `/api/verification/${params.id}`,
    undefined,
    verificationReloadKey
  );
  const accountInfo = useAPI(`/api/account`);
  const tranche2Permission = useAPI("/api/integration/tranche2/settings/can-complete");
  const authContext = useContext(AuthContext);
  const context = useContext(ViewContext);
  const copy = <FontAwesomeIcon icon={faPaste} />;
  const isTranche2Verification = !!verification?.data?.verification?.[0]?.type?.includes(
    "tranche2_v1"
  );
  const publicUrl = `${
    process.env.NODE_ENV === "development"
      ? "http://localhost:3000"
      : "https://identity.stackgo.io"
  }/${
    isTranche2Verification
      ? `public/tranche2/verification/${verification?.data?.verification?.[0].id}`
      : `public/verification/${verification?.data?.verification?.[0].id}`
  }`;

  const verificationInfo = verification?.data?.verification?.[0] || null;
  const canCompleteRiskAssessment = tranche2Permission?.data?.can_complete === true;
  const riskAssessmentDetail = useAPI(
    isTranche2Verification && canCompleteRiskAssessment
      ? `/api/risk-assessment/${params.id}`
      : null
  );
  const riskAssessment = riskAssessmentDetail?.data?.risk_assessment || null;
  const riskAssessmentStatus = isTranche2Verification
    ? deriveTranche2RiskStatus(verificationInfo, riskAssessment)
    : null;
  const riskAssessmentReadyForReview = riskAssessmentStatus === "awaiting_review";
  // Cancellable = admin, not already cancelled, and either the row is still
  // active (KYC and pre-biometric T2) or it is a T2 whose risk assessment is not
  // finalised. A T2 awaiting review sits at active:false, so gating the button on
  // `active` alone hides it for exactly the state staff need to clean up.
  //
  // Keyed on the fetched `riskAssessment` object, NOT riskAssessmentStatus: the
  // derived fallback returns "awaiting_review" for any T2 with a positive result,
  // so a completed T2 would flash the button before the fetch resolves (useAPI
  // starts at { data: null, loading: false }, so `loading` is not a usable
  // "not yet fetched" signal). Anyone who passes permission.admin is
  // master/owner/admin -- the same set canCompleteAssessment short-circuits true
  // for -- so riskAssessmentDetail is always fetched for them.
  const canCancelVerification =
    authContext?.permission?.admin &&
    verificationInfo?.result !== "cancelled" &&
    (verificationInfo?.active ||
      (isTranche2Verification &&
        !!riskAssessment &&
        !["completed", "cancelled"].includes(riskAssessment.status)));
  const canResendInvite = canResendVerification({
    canCancel: canCancelVerification,
    verificationInfo,
  });
  const riskAssessmentWaitingForBiometric = [
    "consent_pending",
    "awaiting_intake",
    "awaiting_biometric",
  ].includes(riskAssessmentStatus);

  // Which optional PDF sections have data for this verification ("when available").
  const {
    adverseMediaResult,
    sanctionsAvailable,
    adverseMediaAvailable,
    imagesAvailable,
  } = derivePdfAvailability(verificationInfo, imagesResponse, imagesUnavailable);

  let images = [];
  useEffect(() => {
    async function getVerificationData() {
      const imageRefs = verification?.data?.images;
      if (!imageRefs || imageRefs.length === 0) {
        // Nothing to fetch — don't leave the loader hanging.
        setImageDataFetched(true);
        return;
      }

      // Fetch each media item independently. Veriff purges verification media
      // after ~45 days, so an expired/missing fetch must not hang the page or
      // throw — we skip it and flag images as unavailable.
      // The images belong to this verification row; the media route names
      // it so the server can check the image is in the caller's account.
      const verificationId = verification?.data?.verification?.[0]?.id;
      const collected = [];
      await Promise.all(
        imageRefs.map(async function (image) {
          try {
            const mediaUrl = veriffMediaUrl(verificationId, image.id);
            if (!mediaUrl) return;
            const mResponse = await Axios.get(mediaUrl);
            if (isUsableMediaResponse(mResponse?.data)) {
              collected.push({
                ...mResponse.data.data,
                name: image.name,
                id: image.id,
              });
            }
          } catch (err) {
            // Media unavailable (e.g. expired/purged after 45 days) — skip it.
            console.log(
              `Media unavailable for image ${image.id}: ${err?.message}`
            );
          }
        })
      );

      setImgagesResponse(collected);
      setImageDataFetched(true);
      setImagesUnavailable(collected.length === 0);
    }

    function settingVerificationData() {
      // Re-derive on every settled fetch so a corrected address shows up after
      // a resend. Bail while a refetch is in flight -- useAPI drops `data` on
      // refetch, and deriving from it would overwrite the populated values
      // with "No Email"/"No Result" placeholders.
      if (!verification?.data?.verification?.[0]) {
        return;
      }
      let updatedVerificationData = {};
      if (verification?.data?.verification?.[0]?.type.includes("full_auto")) {
        console.log(
          "Full Auto Verification",
          verification?.data?.verification?.[0].result_info?.data
        );
        updatedVerificationData = {
          documentType:
            verification?.data?.verification?.[0].result_info?.data
              ?.verification?.document?.type?.value || "No Document",
          email: verification?.data?.verification?.[0].email || "No Email",
          result: verification?.data?.verification?.[0].result || "No Result",
          ip:
            verification?.data?.verification?.[0].result_info?.technicalData
              ?.ip || "No IP",
          documentFirstName:
            verification?.data?.verification?.[0].result_info?.data
              ?.verification?.person?.firstName?.value || "No Name",
          documentLastName:
            verification?.data?.verification?.[0].result_info?.data
              ?.verification?.person?.lastName?.value || "No Name",
          documentIdNumber:
            verification?.data?.verification?.[0].result_info?.data
              ?.verification?.person?.idNumber?.value || "No ID Number",
          dateOfBirth:
            verification?.data?.verification?.[0].result_info?.data
              ?.verification?.person?.dateOfBirth?.value || "No DOB",
          documentNumber:
            verification?.data?.verification?.[0].result_info?.data
              ?.verification?.document?.number?.value || "No Document",
          documentCountry:
            verification?.data?.verification?.[0].result_info?.data
              ?.verification?.document?.country?.value || "NA",
          validFrom:
            verification?.data?.verification?.[0].result_info?.data
              ?.verification?.document?.validFrom?.value || "NA",
          validUntil:
            verification?.data?.verification?.[0].result_info?.data
              ?.verification?.document?.validUntil?.value || "NA",
          decisionTime:
            verification?.data?.verification?.[0].result_info?.acceptanceTime ||
            "NA",
        };
        console.log("Full Auto Verification Data", updatedVerificationData);
      } else {
        updatedVerificationData = {
          documentType:
            verification?.data?.verification?.[0].result_info?.verification
              ?.document?.type || "No Document",
          email: verification?.data?.verification?.[0].email || "No Email",
          result: verification?.data?.verification?.[0].result || "No Result",
          ip:
            verification?.data?.verification?.[0].result_info?.technicalData
              ?.ip || "No IP",
          documentFirstName:
            verification?.data?.verification?.[0].result_info?.verification
              ?.person?.firstName || "No Name",
          documentLastName:
            verification?.data?.verification?.[0].result_info?.verification
              ?.person?.lastName || "No Name",
          documentIdNumber:
            verification?.data?.verification?.[0].result_info?.verification
              ?.person?.idNumber || "No ID Number",
          dateOfBirth:
            verification?.data?.verification?.[0].result_info?.verification
              ?.person?.dateOfBirth || "No DOB",
          documentNumber:
            verification?.data?.verification?.[0].result_info?.verification
              ?.document?.number || "No Document",
          documentCountry:
            verification?.data?.verification?.[0].result_info?.verification
              ?.document?.country || "NA",
          validFrom:
            verification?.data?.verification?.[0].result_info?.verification
              ?.document?.validFrom || "NA",
          validUntil:
            verification?.data?.verification?.[0].result_info?.verification
              ?.document?.validUntil || "NA",
          decisionTime:
            verification?.data?.verification?.[0].result_info?.verification
              ?.decisionTime || "NA",
          reason:
            verification?.data?.verification?.[0].result_info?.verification
              ?.reason || "No reason provided",
        };
      }
      setVerificationData(updatedVerificationData);
    }

    if (!verification.loading && verification.data) {
      getVerificationData();
      settingVerificationData();
      setNavBarItems(
        buildVerificationNavBarItems(
          verification.data.verification[0],
          params,
          { canCompleteRiskAssessment }
        )
      );
    }
  }, [verification, canCompleteRiskAssessment, isTranche2Verification]);

  function downloadPDF() {
    console.log("Download PDF!");
    var doc = new jsPDF("portrait", "px", "a4", "false");
    const pageHeight = doc.internal.pageSize.getHeight();
    const pageWidth = doc.internal.pageSize.getWidth();

    const sessionId = verification?.data?.verification?.[0]?.session_id || "";

    // Standard page header (logo top-right + report title + session), drawn on
    // every page per the template.
    const drawHeader = () => {
      doc.addImage(logo, "PNG", 310, 30, 100, 15);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(16);
      doc.text(50, 40, `Identity Verification Report`);
      doc.setFontSize(9);
      doc.setFont("helvetica", "normal");
      doc.text(50, 50, `Session: ${sessionId}`);
    };

    // Start a fresh page with the standard header and optional section title,
    // returning the y where content should begin.
    const startSectionPage = (title) => {
      doc.addPage();
      drawHeader();
      let y = 75;
      if (title) {
        doc.setFontSize(13);
        doc.setFont("helvetica", "bold");
        doc.text(title, 50, y);
        y += 22;
      }
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      return y;
    };

    drawHeader();
    doc.setFontSize(13);
    doc.setFont("helvetica", "bold");
    doc.text(50, 90, "Report Summary");
    doc.setFontSize(12);
    doc.text(50, 110, "Identity");
    doc.setFont("helvetica", "normal");
    doc.text(50, 125, "First Name");
    doc.text(
      170,
      125,
      `${
        verificationData.documentFirstName
      }`
    );
    doc.text(50, 140, "Last Name");
    doc.text(
      170,
      140,
      `${
        verificationData.documentLastName
      }`
    );
    doc.text(50, 155, `Date of Birth`);
    doc.text(
      170,
      155,
      `${
        verificationData.dateOfBirth
      }`
    );
    doc.setFont("helvetica", "bold");
    doc.text(50, 180, "Verification Result");
    doc.setFont("helvetica", "normal");
    doc.text(50, 195, "Result");
    doc.text(
      170,
      195,
      `${verification?.data?.verification?.[0].result}` || "No Result"
    );

    doc.text(50, 210, "Timestamp");

    doc.text(
      170,
      210,
      verificationData.decisionTime.split("T")[0] || "NA"
    );

    doc.text(50, 225, "Service");
    doc.text(
      170,
      225,
      `Biometric Face and Document Identity Check ${
        verification?.data?.verification?.[0].type.includes("full_auto")
          ? "(Full Auto)"
          : ""
      }`
    );

    doc.text(50, 240, "Liveness test");
    doc.text(170, 240, "Passed");

    doc.text(50, 255, "Data and Device integrity");
    doc.text(170, 255, "Passed");

    doc.setFont("helvetica", "bold");
    doc.text(50, 280, "Prepared for");
    doc.setFont("helvetica", "normal");

    doc.text(50, 295, "Business Name");
    doc.text(
      170,
      295,
      authContext?.user?.accounts?.filter(
        (a) => (a.id = authContext?.user?.account_id)
      )?.[0]?.name || "No Business"
    );
    doc.setFont("helvetica", "bold");
    doc.text(50, 320, "Document");
    doc.setFont("helvetica", "normal");
    doc.text(50, 335, "Type");
    doc.text(
      170,
      335,
      verificationData.documentType
    );
    doc.text(50, 350, "Country");
    doc.text(
      170,
      350,
      verificationData.documentCountry
    );
    doc.text(50, 365, "Number");
    doc.text(
      170,
      365,
      verificationData.documentNumber
    );
    doc.text(50, 380, "Valid From");
    doc.text(
      170,
      380,
      verificationData.validFrom
    );
    doc.text(50, 395, "Valid To");
    doc.text(
      170,
      395,
      verificationData.validUntil
    );

    doc.setFont("helvetica", "bold");
    doc.text(50, 420, "Session");
    doc.setFont("helvetica", "normal");
    doc.text(50, 435, "IP Address");
    doc.text(
      170,
      435,
      verificationData.ip
    );
    doc.text(50, 450, "IP Location");
    doc.text(
      170,
      450,
      verification?.data?.verification?.[0].result_info?.technicalData
        ?.location || "No Location"
    );
    doc.text(50, 465, "Longitude");
    doc.text(
      170,
      465,
      `${verification?.data?.verification?.[0].result_info?.technicalData?.longitude}` ||
        "No Longitude"
    );

    doc.text(50, 480, "Latitude");
    doc.text(
      170,
      480,
      `${verification?.data?.verification?.[0].result_info?.technicalData?.latitude}` ||
        "No Latitude"
    );

    // doc.text(50, 495, "OS");
    // doc.text(
    //   170,
    //   495,
    //   verification?.data?.verification?.[0].result_info?.technicalData?.os ||
    //     "No OS"
    // );

    // doc.text(50, 510, "Browser");
    // doc.text(
    //   170,
    //   510,
    //   verification?.data?.verification?.[0].result_info?.technicalData
    //     ?.browser || "No Browser"
    // );

    // doc.text(50, 525, "Device");
    // doc.text(
    //   170,
    //   525,
    //   verification?.data?.verification?.[0].result_info?.technicalData
    //     ?.device || "No Device"
    // );

    // PEP/Sanctions results (when available and selected) — packed multiple per page
    if (pdfOptions.sanctions && sanctionsAvailable) {
      const foundRecords =
        verificationInfo?.sanctions_results?.found_records || [];
      let y = startSectionPage("PEP/Sanctions Results");

      if (foundRecords.length === 0) {
        doc.text("No Matches Found", 50, y);
      } else {
        const labelX = 50;
        const valueX = 170;
        const valueWidth = pageWidth - valueX - 50;
        const lineHeight = 14;

        foundRecords.forEach((record) => {
          // Pre-measure each record so it isn't split across a page break.
          const rows = formatSanctionsRecord(record).map((row) => ({
            ...row,
            lines: doc.splitTextToSize(String(row.value || ""), valueWidth),
          }));
          const recordHeight =
            rows.reduce(
              (h, row) => h + Math.max(1, row.lines.length) * lineHeight,
              0
            ) + 14; // + divider gap

          if (y + recordHeight > pageHeight - 40) {
            y = startSectionPage("PEP/Sanctions Results");
          }

          rows.forEach((row) => {
            doc.setFont("helvetica", "bold");
            doc.text(String(row.label), labelX, y);
            doc.setFont("helvetica", "normal");
            doc.text(row.lines, valueX, y);
            y += Math.max(1, row.lines.length) * lineHeight;
          });

          // light divider between records
          y += 4;
          doc.setDrawColor(220, 220, 220);
          doc.line(labelX, y, pageWidth - 50, y);
          y += 10;
        });
      }
    }

    // Adverse media results (when available and selected) — packed multiple per page
    if (pdfOptions.adverseMedia && adverseMediaAvailable) {
      const entries = adverseMediaResult?.data || [];
      const suspected =
        adverseMediaResult?.status?.serviceSuspected !== false;
      let y = startSectionPage("Adverse Media Results");

      if (!suspected || entries.length === 0) {
        doc.text("No Matches Found", 50, y);
      } else {
        const maxWidth = pageWidth - 100;
        entries.forEach((entry) => {
          if (y > pageHeight - 80) {
            y = startSectionPage("Adverse Media Results");
          }
          doc.setFont("helvetica", "bold");
          const titleLines = doc.splitTextToSize(
            entry?.title || "Untitled",
            maxWidth
          );
          doc.text(titleLines, 50, y);
          y += titleLines.length * 12 + 2;

          doc.setFont("helvetica", "normal");
          doc.setTextColor(0, 0, 200);
          const urlLines = doc.splitTextToSize(entry?.url || "", maxWidth);
          doc.text(urlLines, 50, y);
          y += urlLines.length * 10 + 2;

          doc.setTextColor(0, 0, 0);
          const textLines = doc.splitTextToSize(entry?.text || "", maxWidth);
          doc.text(textLines, 50, y);
          y += textLines.length * 10 + 10;
        });
        doc.setTextColor(0, 0, 0);
      }
    }

    // Document images (when available and selected)
    if (pdfOptions.images && imagesAvailable) {
      imagesResponse.forEach((image) => {
        const widthRatio = (pageWidth - 80) / image.width;
        const heightRatio = (pageHeight - 100) / image.height;
        const ratio = widthRatio > heightRatio ? heightRatio : widthRatio;

        doc.addPage();
        drawHeader();
        doc.setFontSize(12);
        doc.setFont("helvetica", "bold");
        doc.text(50, 75, image.name);
        doc.setFont("helvetica", "normal");
        doc.text(50, 90, "Date Supplied");
        doc.text(
          170,
          90,
          `${verificationData?.decisionTime?.split?.("T")?.[0] || "NA"}`
        );
        doc.addImage(
          `data:image/png;base64,${image.image}`,
          "PNG",
          50,
          105,
          ratio * image.width,
          ratio * image.height
        );
      });
    }

    doc.save(`Identity Verification Report`);
  }

  function deleteVerification() {
    console.log("Cancel Verification");
    context.modal.show({
      title: 'Cancel  Verification - This action cannot be undone',
      form: {
        email: {
          label: 'Email',
          type: 'hidden',
        }
      },
      buttonText: 'Go',
      url: '/api/verification/cancel/' + params.id,
      method: 'delete',
      }, (form, res) => {
        context.notification.show('Verification Cancelled', 'success', true);
        window.location = '/dashboard';
      }
    );
  }

  function resendVerification() {
    context.modal.show(
      {
        title: "Resend verification email",
        text: "The client receives the same verification link again. Correcting the address updates it on this verification, so reminders follow it too \u2014 but the original link stays valid for whoever already received it. If it reached the wrong person, cancel this verification and start a new one instead.",
        form: {
          email: {
            label: "Send to",
            type: "email",
            required: true,
            value: verificationInfo?.email || "",
            errorMessage: "Please enter a valid email address",
          },
        },
        buttonText: "Resend",
        url: `/api/verification/${params.id}/resend-email`,
        method: "post",
      },
      // No notification here: Form auto-toasts res.data.message on success,
      // and a second call would double it.
      () => setVerificationReloadKey((key) => key + 1)
    );
  }

  return !imageDataFetched &&
    verification.loading &&
    verification.data === undefined &&
    imagesResponse.data === undefined &&
    images.length === 0 ? (
    <Loader></Loader>
  ) : (
    <Fragment>
      <Animate type="pop">
        <Row color="brand" title="Verification Result">
          {" "}
        </Row>

        <Card>
          <Card
            title={`Verification for ${verification?.data?.verification?.[0].first_name} ${verification?.data?.verification?.[0].last_name}`}
          >
            <SubNav items={navBarItems} />

            {["positive", "negative", "review"].includes(verificationInfo?.result) && (
              <div>
                <Button
                  text="Download PDF"
                  action={() => setShowPdfOptions((open) => !open)}
                  outline
                  small
                />
                {showPdfOptions && (
                  <Card title="PDF options">
                    {imagesUnavailable && (
                      <Message type="warning">
                        Document images are no longer available — Veriff retains
                        verification media for 45 days, and this verification is
                        older than that. The report will be generated without
                        images.
                      </Message>
                    )}
                    {imagesAvailable && (
                      <Switch
                        name="images"
                        label="Include images"
                        default={pdfOptions.images}
                        onChange={(name, on) =>
                          setPdfOptions((opts) => ({ ...opts, images: on }))
                        }
                      />
                    )}
                    {sanctionsAvailable && (
                      <Switch
                        name="sanctions"
                        label="Include PEP/Sanctions"
                        default={pdfOptions.sanctions}
                        onChange={(name, on) =>
                          setPdfOptions((opts) => ({ ...opts, sanctions: on }))
                        }
                      />
                    )}
                    {adverseMediaAvailable && (
                      <Switch
                        name="adverseMedia"
                        label="Include adverse media"
                        default={pdfOptions.adverseMedia}
                        onChange={(name, on) =>
                          setPdfOptions((opts) => ({
                            ...opts,
                            adverseMedia: on,
                          }))
                        }
                      />
                    )}
                    <Button text="Generate PDF" action={downloadPDF} small />
                  </Card>
                )}
              </div>
            )}
            <br />

            {verification?.data?.verification?.[0].active && (
              <Message>
                Verification is active. Link: {publicUrl}{" "}
                <span
                  className="sg-icon-copy"
                  onClick={() => {
                    navigator.clipboard.writeText(publicUrl);
                    context.notification.show("Copied!", "success", true);
                  }}
                >
                  {copy}
                </span>
                {(canResendInvite || canCancelVerification) && (
                  <span
                    style={{
                      position: "absolute",
                      right: 0,
                      paddingRight: "20px",
                      display: "flex",
                      alignItems: "center",
                      gap: "14px",
                    }}
                  >
                    {canResendInvite && (
                      <Button
                        icon="mail"
                        title="Resend verification email"
                        action={resendVerification}
                      />
                    )}
                    {canCancelVerification && (
                      <Button
                        icon="trash-2"
                        title="Cancel verification"
                        action={deleteVerification}
                      />
                    )}
                  </span>
                )}
              </Message>
            )}

            {/* A Tranche 2 awaiting the staff review sits at active:false, so the
                active-only Message above never renders for it. Surface the cancel
                action on its own for that case. */}
            {canCancelVerification && !verificationInfo?.active && (
              <Message>
                This request has not been completed and can still be cancelled.
                <span style={{ position: "absolute", right: 0, paddingRight: "20px" }}>
                  <Button
                    icon="trash-2"
                    title="Cancel verification"
                    action={deleteVerification}
                  />
                </span>
              </Message>
            )}

            {isTranche2Verification && canCompleteRiskAssessment && (
              <Message
                type={
                  riskAssessmentReadyForReview ? "warning" : "info"
                }
                title="Tranche 2 Risk Assessment"
              >
                <p>
                  Status: {riskAssessmentStatus || "consent_pending"}
                </p>
                {riskAssessmentReadyForReview && (
                  <p>
                    Action required: this verification is ready for risk assessment completion.
                  </p>
                )}
                {riskAssessmentReadyForReview && canCompleteRiskAssessment && (
                  <p>
                    <Button
                      text="Open Risk Assessment"
                      goto={`/verification/risk-assessment/${params.id}`}
                    />
                  </p>
                )}
                {riskAssessmentWaitingForBiometric && (
                  <p>
                    Waiting for consent, onboarding form, and biometric webhook completion
                    before risk assessment review.
                  </p>
                )}
                {riskAssessmentReadyForReview && !canCompleteRiskAssessment && (
                  <p>
                    Awaiting an assigned assessor. Owner/admin can update eligible users in
                    Tranche 2 settings.
                  </p>
                )}
                {riskAssessment?.edd_active === true && (
                  <p>
                    Enhanced Due Diligence (EDD) is active for this customer.
                    {riskAssessmentDetail?.data?.edd_case_id && (
                      <Button
                        small
                        text="Open EDD case"
                        goto={`/verification/edd/${riskAssessmentDetail.data.edd_case_id}`}
                      />
                    )}
                  </p>
                )}
                {canCompleteRiskAssessment && riskAssessment?.rating && (
                  <p>
                    <Button
                      small
                      text="Open ad-hoc review"
                      action={async () => {
                        const reason = window.prompt(
                          "Reason for this ad-hoc review?"
                        );
                        if (!reason) return;
                        try {
                          const res = await Axios.post(
                            "/api/periodic-review/ad-hoc",
                            { verification_id: params.id, reason }
                          );
                          const id = res?.data?.data?.case?.id;
                          window.location = id
                            ? `/verification/periodic-review/${id}`
                            : "/account/periodic-reviews";
                        } catch (err) {
                          window.alert(
                            err?.response?.data?.message ||
                              "Unable to open ad-hoc review"
                          );
                        }
                      }}
                    />
                  </p>
                )}
              </Message>
            )}

            <p>Document Type: {verificationData.documentType}</p>
            <p>Email: {verificationData.email}</p>
            <p>Result: {verificationData.result}</p>
            {["negative", "resubmission"].includes(verificationData?.result) && (
              <p>Reason: {verificationData?.reason || "No reason provided"}</p>
            )}
            <p>IP: {verificationData.ip}</p>
            <p>Document First Name: {verificationData.documentFirstName}</p>
            <p>Document Last Name: {verificationData.documentLastName}</p>
            <p>Document ID Number {verificationData.documentIdNumber}</p>
            <p>Date Of Birth: {verificationData.dateOfBirth}</p>
            <p>Document Number: {verificationData.documentNumber}</p>
            <p>Document Country: {verificationData.documentCountry}</p>
            <p>Valid From: {verificationData.validFrom}</p>
            <p>Valid Until: {verificationData.validUntil}</p>
            <p>Decision Time: {verificationData.decisionTime}</p>
            {verification?.data?.verification?.[0].type.includes(
              "full_auto"
            ) && (
              <>
                <p>Check Type: Full Auto</p>
                <p>
                  Decision Score: {verification?.data?.verification?.[0].result_info?.data?.verification?.decisionScore}
                </p>
              </>
            )}
            <br />
            {accountInfo?.data?.sanctions_settings?.address_extraction && (
              <>
                <h2>Address Extraction</h2>
                <p>
                  Parsed Address:{" "}
                  {verification?.data?.verification?.[0]?.result_info
                    ?.verification?.person?.addresses?.[0]?.fullAddress ||
                    "No Address Found"}
                </p>
              </>
            )}
            {console.log("FUll response: ", verification)}
            {console.log(verification.data?.images)}
            {console.log("imagesResponse: ", imagesResponse)}

            {imagesResponse === null ||
            verification.data === undefined ||
            imageDataFetched === false ? (
              <Animate type="pop">
                <Row color="white" loading={verification?.loading}>
                  <Card title="Images associated to your verifications will appear here" />
                </Row>
              </Animate>
            ) : imagesUnavailable ? (
              <Message type="warning" title="Images no longer available">
                Veriff retains verification media for 45 days. This
                verification is older than 45 days, so its document images are
                no longer available. You can still download the PDF report
                (without images) using the button above.
              </Message>
            ) : (
              <div>
                {console.log("imagesResponse: ", imagesResponse)}
                <>
                  <p>
                    <b>
                      Important: please note images below will be archived after 45 days.
                    </b>
                    <br />
                      If you need to retain images longer please download the PDF above or speak to us about automation options.
                  </p>
                </>
                {imagesResponse?.map((image, i) => (
                  <Card key={i}>
                    <Image
                      key={i}
                      source={`data:image/png;base64,${image.image}`}
                      title={image.name}
                    />
                    <p>{image.name}</p>
                  </Card>
                ))}

                {/* <img src={`data:image/jpeg;base64,${mediaResponse.data?.image}`} /> */}
              </div>
            )}
          </Card>
        </Card>
      </Animate>
    </Fragment>
  );
}
